import { z } from 'zod';
import {
  ReferralDiagnosis,
  ReferralDiagnosisCondition,
  ReferralDiagnosisSchema,
} from '../ReferralDiagnosisSchema';

// The FHIR (HL7) code system of the Healix diagnosis condition codes.
export const ICD_9_CM_SYSTEM = 'http://hl7.org/fhir/sid/icd-9-cm';

// "00410 - SHIGELLA FLEXNERI" — a 3-5 digit padded ICD-9-CM code (or an
// exact dotted code such as "004.1"), a dash and the condition name.
const conditionEntryRegex = /^(\d{3,5}|\d{3}\.\d{1,2})\s*-\s*(.+)$/;

// Healix sends `diagnosis.conditions` (PPL-3544) and `diagnosis.symptoms`
// (PPL-3548) either in the array form of the base referral body or as one
// semicolon-separated raw string, e.g.
// "00410 - SHIGELLA FLEXNERI; 00730 - INTEST TRICHOMONIASIS". The string
// forms are parsed BEFORE the base rules validate the diagnosis, so both are
// accepted; a diagnosis left with neither conditions nor symptoms parses to no
// diagnosis at all. A malformed entry throws so the API rejects the payload
// (getErrors turns it into the 400 message) instead of silently losing
// diagnosis data.
//
// The same list of coded conditions also reaches us under the name the Healix
// spec gives it, `diagnosis.details` (PPL-3680) — read here as `conditions`,
// which a body stating both wins with.
const toHealixDiagnosis = (value: unknown) => {
  if (!value || typeof value !== 'object') {
    return value;
  }

  const sent = value as {
    conditions?: unknown;
    details?: unknown;
    symptoms?: unknown;
  };

  const sentConditions = sent.conditions ?? sent.details;
  const conditions =
    typeof sentConditions === 'string'
      ? parseHealixDiagnosisConditions(sentConditions)
      : sentConditions;
  const symptoms = parseHealixDiagnosisSymptoms(sent.symptoms);

  const diagnosis: ReferralDiagnosis = {};
  if (Array.isArray(conditions) && conditions.length > 0) {
    diagnosis.conditions = conditions;
  }
  if (symptoms.length > 0) {
    diagnosis.symptoms = symptoms;
  }

  return diagnosis.conditions === undefined && diagnosis.symptoms === undefined
    ? undefined
    : diagnosis;
};

export const ReferralHealixDiagnosisSchema = z.preprocess(
  toHealixDiagnosis,
  ReferralDiagnosisSchema,
);

// Converts the raw Healix conditions string into the coded array form of the
// base referral body:
// [{ system: <ICD-9-CM>, code: '004.1', name: 'Shigella flexneri' }, ...].
// Throws an Error naming the offending entry when it does not follow the
// "<numeric code> - <name>" format.
export function parseHealixDiagnosisConditions(
  conditions: string,
): ReferralDiagnosisCondition[] {
  return conditions
    .split(';')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .map((entry) => {
      const entryMatch = entry.match(conditionEntryRegex);
      if (!entryMatch) {
        throw new Error(
          `Diagnosis conditions entry "${entry}" should be in the "00410 - CONDITION NAME" format`,
        );
      }

      const [, rawCode, rawName] = entryMatch;
      return {
        system: ICD_9_CM_SYSTEM,
        code: normalizeIcd9Code(rawCode),
        name: normalizeConditionName(rawName),
      };
    });
}

// Converts the raw Healix symptoms string into the free-text array form of
// the base referral body: ['Persistent cough', 'Unexplained weight loss'].
// Every entry is trimmed and its inner whitespace collapsed, in the array
// form as much as in the string one, so both sent forms are stored
// identically. Entries blank after trimming are dropped; a non-text entry
// throws so the payload is rejected rather than coerced into nonsense.
//
// A semicolon OR a comma separates the entries: the Healix spec (v1.2) writes
// the list both ways ("Skin lesion, Symptom 2"), so a comma cannot be part of
// a symptom's own text.
const SYMPTOM_SEPARATORS = /[;,]/;

export function parseHealixDiagnosisSymptoms(symptoms: unknown): string[] {
  const entries =
    typeof symptoms === 'string'
      ? symptoms.split(SYMPTOM_SEPARATORS)
      : symptoms;
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .map((entry) => {
      if (typeof entry !== 'string') {
        throw new Error(
          `Diagnosis symptoms entry ${JSON.stringify(entry)} should be text`,
        );
      }

      return trimAndCollapseWhitespace(entry);
    })
    .filter((symptom) => symptom.length > 0);
}

// ICD-9-CM codes arrive in the short format without the decimal point and
// zero-padded on the right, e.g. "00410" for 004.1. Restores the decimal
// point after the 3-digit category and strips the padding zeros. Codes that
// already contain the decimal point are exact and kept as sent.
const normalizeIcd9Code = (rawCode: string): string => {
  if (rawCode.length <= 3 || rawCode.includes('.')) {
    return rawCode;
  }

  const category = rawCode.slice(0, 3);
  const subdivision = rawCode.slice(3).replace(/0+$/, '');
  return subdivision.length > 0 ? `${category}.${subdivision}` : category;
};

// The base schema trims every diagnosis string for every organization; this
// goes further and collapses the runs of whitespace INSIDE the text too,
// which Healix's padded exports are full of ("SHIGELLA  FLEXNERI").
const trimAndCollapseWhitespace = (rawText: string): string =>
  rawText.trim().replace(/\s+/g, ' ');

// "SHIGELLA FLEXNERI" -> "Shigella flexneri". Only fully upper-cased names
// are sentence-cased, so a mixed-case name (e.g. a proper noun spelled
// intentionally) is kept as sent. Symptoms are deliberately left out of this:
// they are free text where lower-casing would mangle the acronyms (CT, MRI,
// GP) that clinical notes are full of.
const normalizeConditionName = (rawName: string): string => {
  const name = trimAndCollapseWhitespace(rawName);
  if (/[a-z]/.test(name)) {
    return name;
  }

  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};
